import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import axios, { AxiosRequestConfig, AxiosError } from 'axios';
import * as Sentry from '@sentry/react';

// ✅ Generate CSRF token (or get from cookie)
const getCSRFToken = (): string | null => {
  // Check if token exists in cookie
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'csrf_token') {
      return value;
    }
  }
  
  // Generate new token if not exists
  const token = Array.from(crypto.getRandomValues(new Uint8Array(16)))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
  
  // Store in cookie
  document.cookie = `csrf_token=${token}; path=/; SameSite=Strict`;
  return token;
};

export const axiosBaseQuery = (
  { baseUrl }: { baseUrl: string } = { baseUrl: '' }
): BaseQueryFn => async (args, api, extraOptions) => {
  const { url, method = 'GET', data, params, headers } = args;

  try {
    // Get token from state
    const token = (api.getState() as any).auth?.token;
    
    console.log(`🌐 API Request: ${method} ${baseUrl}${url}`);

    const result = await axios({
      url: baseUrl + url,
      method,
      data,
      params,
      headers: {
        ...headers,
        // ✅ ADD AUTHORIZATION TOKEN
        ...(token && { Authorization: `Bearer ${token}` }),
        // ✅ ADD CSRF TOKEN FOR POST/PUT/PATCH/DELETE
        ...(method !== 'GET' && { 'X-CSRF-Token': getCSRFToken() }),
        // ✅ ADD CONTENT TYPE
        'Content-Type': 'application/json',
      },
      // ✅ SEND COOKIES
      withCredentials: true,
    });

    console.log(`✅ API Success: ${method} ${url}`, result.data);
    return { data: result.data };
  } catch (axiosError) {
    const err = axiosError as AxiosError;
    console.error(`❌ API Error: ${method} ${url}`, {
      status: err.response?.status,
      data: err.response?.data,
      message: err.message,
    });

    // Log error to Sentry
    Sentry.captureException(err);

    return {
      error: {
        status: err.response?.status,
        data: err.response?.data || err.message,
        message: err.message,
      },
    };
  }
};
